/**
 * Lease create/edit — one component for both `new` and `:id/edit`, switched on
 * whether `useParams().id` is present (same shape as PropertyFormPage/
 * TenantFormPage). Create uses `POST tenants/leases/`, matching
 * `leaseCreateSchema` (refactor/backend/src/modules/tenants/serializers.ts:154-164);
 * edit uses `PATCH tenants/leases/{id}/`. Unlike TenantFormPage there is no
 * role-dependent response shape here — `getLease` always returns the same
 * `Lease` shape, so every field maps straight onto the form.
 *
 * `lease_document` upload is a separate action, `POST leases/{id}/upload_document/`
 * (mirrors PropertyDetailPage's image-upload control), so it only appears once
 * the lease already has an id — i.e. in edit mode, after the first save.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { parseApiError } from '../../lib/apiError';
import {
  createLease,
  getLease,
  updateLease,
  uploadLeaseDocument,
} from '../../api/endpoints/tenants';
import { LEASE_STATUSES, type LeaseStatus } from '../../types/lease';

const INITIAL_FORM = {
  unit: '',
  tenant: '',
  start_date: '',
  end_date: '',
  rent_amount: '',
  security_deposit: '',
  status: 'PENDING' as LeaseStatus,
  notes: '',
};

type FormState = typeof INITIAL_FORM;

export function LeaseFormPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const isEdit = id !== undefined;
  const leaseId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [selectedDocumentFile, setSelectedDocumentFile] = useState<File | null>(null);
  const [documentInputKey, setDocumentInputKey] = useState(0);

  const existing = useQuery({
    queryKey: ['lease', leaseId],
    queryFn: () => getLease(leaseId),
    enabled: isEdit && Number.isFinite(leaseId),
  });

  const uploadDocument = useMutation({
    mutationFn: (file: File) => uploadLeaseDocument(leaseId, file),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['lease', leaseId] });
      setSelectedDocumentFile(null);
      // Remounts the file input — the only reliable way to clear a `type="file"`
      // value, matching PropertyDetailPage's image-upload control.
      setDocumentInputKey((key) => key + 1);
    },
  });

  function handleDocumentUploadSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (selectedDocumentFile !== null) uploadDocument.mutate(selectedDocumentFile);
  }

  useEffect(() => {
    if (existing.data === undefined) return;
    const lease = existing.data;
    setForm({
      unit: String(lease.unit),
      tenant: String(lease.tenant),
      start_date: lease.start_date ?? '',
      end_date: lease.end_date ?? '',
      rent_amount: lease.rent_amount,
      security_deposit: lease.security_deposit,
      status: lease.status,
      notes: lease.notes,
    });
  }, [existing.data]);

  function update(field: keyof FormState, value: string): void {
    setForm((previous) => ({ ...previous, [field]: value }));
  }

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        unit: Number(form.unit),
        tenant: Number(form.tenant),
        start_date: form.start_date,
        end_date: form.end_date,
        rent_amount: form.rent_amount,
        security_deposit: form.security_deposit === '' ? undefined : form.security_deposit,
        status: form.status,
        notes: form.notes,
      };
      return isEdit ? updateLease(leaseId, payload) : createLease(payload);
    },
    onSuccess: () => navigate('..', { replace: true }),
    onError: (caught) => {
      const parsed = parseApiError(caught);
      setError(parsed.message);
      setFieldErrors(parsed.fieldErrors);
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    save.mutate();
  }

  function fieldError(field: string): string | undefined {
    return fieldErrors[field]?.[0];
  }

  if (isEdit && existing.isPending) return <p>Loading…</p>;
  if (isEdit && existing.isError) {
    return (
      <p role="alert" className="form__error">
        {parseApiError(existing.error).message}
      </p>
    );
  }

  return (
    <section>
      <h2>{isEdit ? 'Edit lease' : 'New lease'}</h2>

      <form onSubmit={handleSubmit} noValidate>
        {error !== null && (
          <p role="alert" className="form__error">
            {error}
          </p>
        )}

        <label htmlFor="lease-unit">Unit id</label>
        <input
          id="lease-unit"
          type="number"
          required
          value={form.unit}
          onChange={(event) => update('unit', event.target.value)}
        />
        {fieldError('unit') !== undefined && <p className="form__field-error">{fieldError('unit')}</p>}

        <label htmlFor="lease-tenant">Tenant id</label>
        <input
          id="lease-tenant"
          type="number"
          required
          value={form.tenant}
          onChange={(event) => update('tenant', event.target.value)}
        />
        {fieldError('tenant') !== undefined && (
          <p className="form__field-error">{fieldError('tenant')}</p>
        )}

        <label htmlFor="lease-start-date">Start date</label>
        <input
          id="lease-start-date"
          type="date"
          required
          value={form.start_date}
          onChange={(event) => update('start_date', event.target.value)}
        />

        <label htmlFor="lease-end-date">End date</label>
        <input
          id="lease-end-date"
          type="date"
          required
          value={form.end_date}
          onChange={(event) => update('end_date', event.target.value)}
        />

        <label htmlFor="lease-rent">Rent amount</label>
        <input
          id="lease-rent"
          required
          value={form.rent_amount}
          onChange={(event) => update('rent_amount', event.target.value)}
        />

        <label htmlFor="lease-deposit">Security deposit</label>
        <input
          id="lease-deposit"
          value={form.security_deposit}
          onChange={(event) => update('security_deposit', event.target.value)}
        />

        <label htmlFor="lease-status">Status</label>
        <select
          id="lease-status"
          value={form.status}
          onChange={(event) => update('status', event.target.value)}
        >
          {LEASE_STATUSES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        {!isEdit && form.status === 'ACTIVE' && (
          <p role="note">
            Creating this lease as ACTIVE marks the unit occupied and bills the tenant a prorated
            first-month invoice immediately.
          </p>
        )}

        <label htmlFor="lease-notes">Notes</label>
        <textarea
          id="lease-notes"
          value={form.notes}
          onChange={(event) => update('notes', event.target.value)}
        />

        <button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : isEdit ? 'Save' : 'Create lease'}
        </button>
      </form>

      {isEdit && (
        <>
          <h3>Lease document</h3>
          {existing.data?.lease_document !== null && existing.data?.lease_document !== undefined && (
            <p>
              Current file: <code>{existing.data.lease_document}</code>
            </p>
          )}
          <form onSubmit={handleDocumentUploadSubmit} noValidate>
            {uploadDocument.isError && (
              <p role="alert" className="form__error">
                {parseApiError(uploadDocument.error).message}
              </p>
            )}
            <label htmlFor="lease-document-upload">Upload document (PDF, DOC, DOCX)</label>{' '}
            <input
              key={documentInputKey}
              id="lease-document-upload"
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={(event) => setSelectedDocumentFile(event.target.files?.[0] ?? null)}
            />{' '}
            <button type="submit" disabled={selectedDocumentFile === null || uploadDocument.isPending}>
              {uploadDocument.isPending ? 'Uploading…' : 'Upload'}
            </button>
          </form>
        </>
      )}
    </section>
  );
}

export default LeaseFormPage;
